import type { QueryClient } from "@tanstack/react-query";
import axios from "axios";

import { api } from "@/api/instance";
import {
  getProfileMeta,
  patchProfileMeta,
  setProfileMeta,
} from "@/entities/profile";
import { qk } from "@/shared/api/queryKeys";
import { settled } from "@/shared/lib/settled";
import {
  isNonEmptyProfileData,
  normalizeProfileData,
} from "../components/ProfileView";
import {
  takeTourHeldProfiles,
  type TourHeldProfile,
} from "@/features/demo-tour";
import type { Profile } from "../types";

type ProfilesListResult = { profiles: Profile[] };

/**
 * Apply success + load presentation for held tour profiles, then clear hold.
 */
export async function revealTourHeldProfiles(qc: QueryClient): Promise<void> {
  const held = takeTourHeldProfiles();
  await Promise.all(held.map((entry) => revealOne(qc, entry)));
}

function applyPendingTitleIfUnchanged(
  qc: QueryClient,
  entry: TourHeldProfile,
): void {
  const pendingName = entry.pendingName;
  if (!pendingName) return;

  qc.setQueryData<ProfilesListResult>(qk.profiles.list(), (prev) => {
    if (!prev) return prev;
    return {
      profiles: prev.profiles.map((p) => {
        if (p.id !== entry.id) return p;
        // User renamed during the tour (pencil step) — keep their title.
        if (p.title !== entry.displayTitle) return p;
        return { ...p, title: pendingName };
      }),
    };
  });
}

async function revealOne(
  qc: QueryClient,
  entry: TourHeldProfile,
): Promise<void> {
  const profileId = entry.id;
  const meta = getProfileMeta(qc, profileId);
  if (!meta) return;

  applyPendingTitleIfUnchanged(qc, entry);

  patchProfileMeta(qc, profileId, {
    status: "success",
    taskId: undefined,
    isCreating: false,
    isInDatabase: true,
    isTrackingStatus: true,
    errorMessage: null,
    isLoadingProfileData: true,
  });

  const result = await settled(api.v1.getProfile(profileId));
  if (getProfileMeta(qc, profileId) === undefined) return;

  if (!result.ok) {
    const err = result.error;
    const httpStatus = axios.isAxiosError(err) ? err.response?.status : undefined;
    const isServerError = httpStatus === undefined || httpStatus >= 500;
    const current = getProfileMeta(qc, profileId) ?? {};
    setProfileMeta(qc, profileId, {
      ...current,
      ...(isServerError
        ? {
            status: "error",
            errorMessage:
              "Не удалось загрузить анкету профиля. Обновите страницу.",
          }
        : {}),
      hasLoadedProfileData: true,
      isLoadingProfileData: false,
    });
    return;
  }

  const presentatorData = result.value.data as unknown;
  const current = getProfileMeta(qc, profileId) ?? {};
  if (!isNonEmptyProfileData(presentatorData)) {
    setProfileMeta(qc, profileId, {
      ...current,
      profileData: undefined,
      hasLoadedProfileData: true,
      isLoadingProfileData: false,
    });
    return;
  }

  setProfileMeta(qc, profileId, {
    ...current,
    status: "success",
    profileData: normalizeProfileData(presentatorData),
    hasLoadedProfileData: true,
    isLoadingProfileData: false,
  });
}
